<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.DIALOG.AMOUNT_REQUESTED"
    :handler="
      (data) => {
        showAmountDialog(data);
      }
    "
  />

  <DialogComponent
    ref="modal"
    type="modal"
    mainClass="fixed z-[61]"
    @closed="
      () => {
        if (currentResolve) {
          currentResolve({ accepted: false });
          currentResolve = null;
        }
        resetDialog();
      }
    "
  >
    <div class="py-2">
      <div class="text-xl font-bold mb-5">
        {{ dialogData.title || 'Sélectionnez le montant' }}
      </div>

      <div v-if="dialogData.message" class="mb-5 text-gray-600">
        {{ dialogData.message }}
      </div>

      <!-- Product Info -->
      <div v-if="dialogData.product" class="flex gap-4 mb-6 pb-6 border-b">
        <img
          v-if="dialogData.product.logoUrls && dialogData.product.logoUrls[0]"
          :src="dialogData.product.logoUrls[0]"
          :alt="dialogData.product.productName"
          class="w-16 h-16 object-contain rounded-xl"
        />
        <div v-else-if="dialogData.product.image" class="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-xl">
          <img :src="dialogData.product.image" :alt="dialogData.product.name" class="max-h-full max-w-full object-contain" />
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">{{ dialogData.product.productName || dialogData.product.name }}</h3>
          <p class="text-sm text-gray-500">{{ dialogData.product.brand?.brandName || dialogData.product.description }}</p>
        </div>
      </div>

      <!-- FIXED Denominations -->
      <div v-if="dialogData.denominationType === 'FIXED' && dialogData.fixedDenominations">
        <p class="text-sm text-gray-600 mb-4">Montant disponible:</p>
        <div class="grid grid-cols-2 gap-3 mb-6">
          <button
            v-for="(amount, index) in dialogData.fixedDenominations"
            :key="index"
            @click="selectedAmount = amount.value"
            class="py-3 px-4 rounded-xl border-2 text-center transition-all"
            :class="selectedAmount === amount.value ? 'border-fuchsia-800 bg-fuchsia-50' : 'border-gray-200 hover:border-fuchsia-300'"
          >
            <p class="font-bold text-gray-900">{{ formatPrice(amount.value) }} {{ dialogData.currency || 'FCFA' }}</p>
            <p v-if="amount.recipientValue" class="text-xs text-gray-500">{{ amount.recipientValue }} {{ dialogData.recipientCurrency || 'USD' }}</p>
          </button>
        </div>
      </div>

      <!-- RANGE Denominations -->
      <div v-else-if="dialogData.denominationType === 'RANGE' || dialogData.minAmount !== undefined">
        <p class="text-sm text-gray-600 mb-4">
          Entrez le montant ({{ formatPrice(dialogData.minAmount) }} - {{ formatPrice(dialogData.maxAmount) }} {{ dialogData.currency || 'FCFA' }}):
        </p>
        <input
          v-model.number="selectedAmount"
          type="number"
          :min="dialogData.minAmount"
          :max="dialogData.maxAmount"
          class="w-full border-2 border-gray-200 rounded-xl p-4 text-lg font-semibold text-center focus:border-fuchsia-600 outline-none"
        />
        <p v-if="dialogData.exchangeRate" class="text-sm text-gray-500 mt-2 text-center">
          ≈ {{ formatPrice(selectedAmount / dialogData.exchangeRate) }} {{ dialogData.recipientCurrency || 'USD' }}
        </p>
      </div>

      <!-- Simple amount preset buttons -->
      <div v-else-if="dialogData.amountPresets && dialogData.amountPresets.length > 0">
        <p class="text-sm text-gray-600 mb-4">Montant:</p>
        <div class="grid grid-cols-3 gap-3 mb-6">
          <button
            v-for="amount in dialogData.amountPresets"
            :key="amount"
            @click="selectedAmount = amount"
            class="py-3 px-4 rounded-xl border-2 text-center transition-all"
            :class="selectedAmount === amount ? 'border-fuchsia-800 bg-fuchsia-50' : 'border-gray-200 hover:border-fuchsia-300'"
          >
            <p class="font-bold text-gray-900">{{ formatPrice(amount) }} {{ dialogData.currency || 'FCFA' }}</p>
          </button>
        </div>
        <div v-if="dialogData.allowCustomAmount" class="mt-4">
          <input
            v-model.number="customAmount"
            type="number"
            :min="dialogData.minAmount"
            :max="dialogData.maxAmount"
            placeholder="Autre montant"
            class="w-full border-2 border-gray-200 rounded-xl p-3 text-center focus:border-fuchsia-600 outline-none"
            @input="selectedAmount = customAmount"
          />
        </div>
      </div>

      <!-- Email Input -->
      <div v-if="dialogData.requireEmail !== false" class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Adresse e-mail</label>
        <input
          v-model="email"
          type="email"
          :placeholder="dialogData.emailPlaceholder || 'votre@email.com'"
          class="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-fuchsia-600 outline-none"
        />
      </div>

      <!-- Quantity Input -->
      <div v-if="dialogData.allowQuantity" class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Quantité</label>
        <div class="flex items-center gap-4 border-2 border-gray-200 rounded-xl p-3">
          <button @click="decrementQuantity" class="px-4 py-2 hover:bg-gray-100 transition-colors">-</button>
          <span class="px-4 font-semibold">{{ quantity }}</span>
          <button @click="incrementQuantity" class="px-4 py-2 hover:bg-gray-100 transition-colors">+</button>
        </div>
      </div>
    </div>

    <div class="flex gap-4 mt-5 justify-end">
      <button
        class="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
        @click="cancelDialog"
      >
        {{ dialogData.cancelText || 'Annuler' }}
      </button>
      <button
        class="px-6 py-2 rounded-lg bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white hover:from-fuchsia-900 hover:to-purple-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!selectedAmount || (dialogData.requireEmail !== false && !email)"
        @click="confirmDialog"
      >
        {{ dialogData.successText || 'Confirmer' }}
      </button>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const dialogData = ref({
  title: '',
  message: '',
  product: null,
  denominationType: null,
  fixedDenominations: [],
  minAmount: 0,
  maxAmount: 0,
  amountPresets: [],
  allowCustomAmount: false,
  currency: 'FCFA',
  recipientCurrency: 'USD',
  exchangeRate: 1,
  requireEmail: true,
  emailPlaceholder: 'votre@email.com',
  allowQuantity: false,
  successText: 'Confirmer',
  cancelText: 'Annuler'
});
const selectedAmount = ref(null);
const customAmount = ref(null);
const email = ref('');
const quantity = ref(1);
let currentResolve = null;
let currentReject = null;

const incrementQuantity = () => {
  quantity.value++;
};

const decrementQuantity = () => {
  if (quantity.value > 1) quantity.value--;
};

const confirmDialog = () => {
  if (currentResolve) {
    currentResolve({
      accepted: true,
      data: {
        amount: selectedAmount.value,
        email: email.value,
        quantity: quantity.value,
        product: dialogData.value.product
      }
    });
    currentResolve = null;
    currentReject = null;
  }
  resetDialog();
  proxy.$refs.modal?.hide();
};

const cancelDialog = () => {
  if (currentResolve) {
    currentResolve({ accepted: false });
    currentResolve = null;
    currentReject = null;
  }
  resetDialog();
  proxy.$refs.modal?.hide();
};

const resetDialog = () => {
  dialogData.value = {
    title: '',
    message: '',
    product: null,
    denominationType: null,
    fixedDenominations: [],
    minAmount: 0,
    maxAmount: 0,
    amountPresets: [],
    allowCustomAmount: false,
    currency: 'FCFA',
    recipientCurrency: 'USD',
    exchangeRate: 1,
    requireEmail: true,
    emailPlaceholder: 'votre@email.com',
    allowQuantity: false,
    successText: 'Confirmer',
    cancelText: 'Annuler'
  };
  selectedAmount.value = null;
  customAmount.value = null;
  email.value = '';
  quantity.value = 1;
};

const formatPrice = (price) => {
  if (!price) return '0';
  return new Intl.NumberFormat('fr-FR').format(Math.round(price));
};

const showAmountDialog = async (config) => {
  return new Promise((resolve, reject) => {
    currentResolve = resolve;
    currentReject = reject;

    dialogData.value = {
      title: config.title || 'Sélectionnez le montant',
      message: config.message || '',
      product: config.product || null,
      denominationType: config.denominationType || null,
      fixedDenominations: config.fixedDenominations || [],
      minAmount: config.minAmount || 0,
      maxAmount: config.maxAmount || 0,
      amountPresets: config.amountPresets || [],
      allowCustomAmount: config.allowCustomAmount || false,
      currency: config.currency || 'FCFA',
      recipientCurrency: config.recipientCurrency || 'USD',
      exchangeRate: config.exchangeRate || 1,
      requireEmail: config.requireEmail !== undefined ? config.requireEmail : true,
      emailPlaceholder: config.emailPlaceholder || 'votre@email.com',
      allowQuantity: config.allowQuantity || false,
      successText: config.successText || 'Confirmer',
      cancelText: config.cancelText || 'Annuler'
    };

    // Set default amount
    if (config.denominationType === 'FIXED' && config.fixedDenominations?.[0]) {
      selectedAmount.value = config.fixedDenominations[0].value;
    } else if (config.minAmount) {
      selectedAmount.value = config.minAmount;
    } else if (config.amountPresets?.[0]) {
      selectedAmount.value = config.amountPresets[0];
    } else {
      selectedAmount.value = null;
    }

    email.value = config.defaultEmail || '';
    quantity.value = config.defaultQuantity || 1;

    proxy.$refs.modal.show();
  });
};

// Exposer la fonction globalement
onMounted(() => {
  window.amountDialog = showAmountDialog;
});

onBeforeUnmount(() => {
  delete window.amountDialog;
});
</script>
